import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, {forwardRef, useEffect, useRef, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {
  GetConfirmViewResponse,
  GetShareableLinksResponse,
} from '../../../../api/types/production.type';
import {
  useConfirmProduction,
  useDownloadProduction,
  useGetConfirmView,
  useGetShareableLinks,
  useProductions,
} from '../../../../hooks/productions/useProductions';
import {RootState} from '../../../../redux/store';
import {formatDateCommon} from '../../../../utils/utils';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import './QualityControlAndConfirmation.scss';
import {
  ModuleType,
  ProductionState,
  ProductionStatus,
  ToastType,
} from '../CreateNewProductionUtils';
import ErrorGridModal from '../generate/ErrorModal/ErrorGridModal';
import {ErrorGridType} from '../generate/ErrorModal/utils';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface QualityControlAndConfirmationProps {
  productionId: number;
  isProductionSet: boolean;
  getStatus: (showToast: boolean, status: ToastType, message?: string) => void;
  goBack?: () => void;
  productionName?: string;
  productionStatusCode?: string;
  productionSetId?: number;
}

const QualityControlAndConfirmation = forwardRef<
  StepChildRef,
  QualityControlAndConfirmationProps
>(
  (
    {
      productionId,
      isProductionSet,
      productionName,
      productionStatusCode,
      productionSetId: productionSetIdFromProps,
      getStatus,
      goBack: _goBack,
    },
    _ref
  ) => {
    const {t} = useTranslation();

    const [showShareableLinksModal, setShowShareableLinksModal] =
      useState<boolean>(false);
    const [QCConfirmationData, setQCConfirmationData] =
      useState<GetConfirmViewResponse>();
    const [shareableLinks, setShareableLinks] =
      useState<GetShareableLinksResponse>();
    const [disabledCommitOrUncommit, setDisabledCommitOrUncommit] =
      useState<boolean>(false);
    const [isAutoRefresh, setIsAutoRefresh] = useState<boolean>(false);
    const [openErrorGridModal, setOpenErrorGridModal] = useState(false);

    const downloadMenuRef = useRef<ArrMenuHandle | null>(null);

    const selectedSecurityGroupId = useSelector(
      (state: RootState) => state.production.securityGroupId
    );
    const productionSetIdFromStore = useSelector(
      (state: RootState) => state.production.productionSetId
    );
    const productionSetId =
      productionSetIdFromProps || productionSetIdFromStore;

    // Determine moduleType from isProductionSet using enum
    const moduleType = useMemo(
      () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
      [isProductionSet]
    );

    const {data: confirmationData, refetch: refetchConfirmView} =
      useGetConfirmView(
        {
          productionId: productionId,
          securityGroupId: selectedSecurityGroupId,
          ModuleType: moduleType,
        },
        !!selectedSecurityGroupId && !!productionId
      );

    const {mutate: getShareableLinks} = useGetShareableLinks(
      (data) => setShareableLinks(data), // onSuccess
      (error) => {
        setShowShareableLinksModal(false);
        getStatus(true, ToastType.Error, error?.message);
      }
    );

    const {mutate: downloadProduction} = useDownloadProduction();
    const {mutate: commitUncommitProduction} = useConfirmProduction();
    const {data: productionsList} = useProductions(
      {
        securityGrpId: selectedSecurityGroupId,
        setId: productionSetId,
        Type: 'Tile',
        filterBy: 'COMPLETED,INPROGRESS',
        sortBy: 'ProductionDate',
        pageSize: -1,
        pageNumber: 1,
      },
      isProductionSet && !!selectedSecurityGroupId && !!productionSetId
    );
    const currentStateCode =
      QCConfirmationData?.productionResultDetails?.productionStateCode;
    const currentStatusCode =
      QCConfirmationData?.productionResultDetails?.productionStatusCode ??
      productionStatusCode;
    const isFailedState =
      currentStateCode?.includes(ProductionState.FAILED) ?? false;
    const isCommitAction =
      currentStateCode === ProductionState.UNCOMMITFAILED
        ? false
        : currentStateCode === ProductionState.COMMITFAILED
          ? true
          : (QCConfirmationData?.isCommitOrUncommit ?? false);

    useEffect(() => {
      if (confirmationData) {
        setQCConfirmationData(confirmationData);
      }
    }, [confirmationData]);

    useEffect(() => {
      if (!QCConfirmationData) return;
      const shouldAutoRefresh =
        !isFailedState &&
        (currentStatusCode === ProductionStatus.INPROGRESS ||
          currentStateCode?.endsWith(ProductionState.INPROGRESS));

      if (!isAutoRefresh && shouldAutoRefresh) {
        setIsAutoRefresh(true);
      }
    }, [
      QCConfirmationData,
      currentStatusCode,
      currentStateCode,
      isFailedState,
      isAutoRefresh,
    ]);

    useEffect(() => {
      if (showShareableLinksModal && productionId) {
        getShareableLinks({
          productionId,
          expiryDate:
            QCConfirmationData?.productionResultDetails
              .shareableLinksExpireOn ?? '',
          isRegenerate: false,
        });
      }
    }, [productionId, showShareableLinksModal]);

    useEffect(() => {
      if (!isAutoRefresh) return;

      const interval = setInterval(async () => {
        const refreshed = await refetchConfirmView();

        const status =
          refreshed?.data?.productionResultDetails?.productionStatusCode;
        const state =
          refreshed?.data?.productionResultDetails?.productionStateCode;

        if (
          status === ProductionStatus.COMPLETED ||
          status === ProductionStatus.DRAFT
        ) {
          clearInterval(interval);
          setIsAutoRefresh(false);
          setDisabledCommitOrUncommit(false);
          setQCConfirmationData(refreshed.data);
        } else if (
          state === ProductionState.UNCOMMITFAILED ||
          state === ProductionState.COMMITFAILED
        ) {
          clearInterval(interval);
          setIsAutoRefresh(false);
          setDisabledCommitOrUncommit(false);
          setQCConfirmationData(refreshed.data);
          getStatus(
            true,
            ToastType.Error,
            t(
              'productionsAndExports.createNewProduction.QCConfirmation.errors.uncommitFailed'
            )
          );
        }
      }, 5000);

      return () => clearInterval(interval);
    }, [isAutoRefresh, refetchConfirmView]);

    const downloadMenu = useMemo(() => {
      return [
        {
          id: 'downloadProduction',
          label: t(
            'productionsAndExports.createNewProduction.QCConfirmation.downloadFullProduction'
          ),
          disabled:
            !QCConfirmationData?.productionResultDetails?.productionArchivePath,
          onClick: () => handleDownloadAllFile(),
        },
        {
          id: 'downloadDat',
          label: t(
            'productionsAndExports.createNewProduction.QCConfirmation.downloadDat'
          ),
          disabled:
            !QCConfirmationData?.productionResultDetails?.productionDATFilePath,
          onClick: () => handleDownloadDAT(),
        },
        {
          id: 'copyPath',
          label: t(
            'productionsAndExports.createNewProduction.QCConfirmation.copyPath'
          ),
          disabled: false,
          onClick: () => handleCopyToClipboard(),
        },
      ];
    }, [QCConfirmationData]);

    /**  Copies text to clipboard */
    const handleCopyToClipboard = () => {
      navigator.clipboard.writeText(
        QCConfirmationData?.productionResultDetails
          ?.productionDestinationPath ?? ''
      );
    };

    const handleDownloadDAT = () => {
      downloadProduction(
        {
          sourcePath:
            QCConfirmationData?.productionResultDetails
              ?.productionDATFilePath ?? '',
          fileName: `${productionName}_DAT`,
        },
        {
          onSuccess: () => {
            getStatus(
              true,
              ToastType.Success,
              t(
                'productionsAndExports.createNewProduction.QCConfirmation.datDownload'
              )
            );
          },
          onError: (error) => {
            getStatus(true, ToastType.Error, error?.message);
          },
        }
      );
    };

    const handleDownloadAllFile = () => {
      downloadProduction(
        {
          sourcePath:
            QCConfirmationData?.productionResultDetails
              ?.productionArchivePath ?? '',
          fileName: `${productionName}`,
        },
        {
          onSuccess: () => {
            getStatus(
              true,
              ToastType.Success,
              t(
                'productionsAndExports.createNewProduction.QCConfirmation.allFileDownload'
              )
            );
          },
          onError: (error) => {
            getStatus(true, ToastType.Error, error?.message);
          },
        }
      );
    };

    const handleCommitUncommit = (isCommit: boolean) => {
      setDisabledCommitOrUncommit(true);
      const payload = {
        ProductionId: productionId,
        SecurityGroupId: selectedSecurityGroupId,
        ProductionLabel: productionName ?? '',
        IsCommit: isCommit,
      };
      commitUncommitProduction(payload, {
        onSuccess: (data) => {
          getStatus(true, ToastType.Success, data.message);
          setIsAutoRefresh(true);
        },
        onError: (error) => {
          getStatus(true, ToastType.Error, error?.message);
          setDisabledCommitOrUncommit(false);
        },
      });
    };

    const onDownloadBtnClick = (e: React.MouseEvent<HTMLElement>) => {
      downloadMenuRef.current?.open(e.currentTarget as HTMLElement);
    };
    const onMenuItemClick = (item: CustomMenuItem) => {
      item.onClick?.();
    };

    const handleRegenerate = async () => {
      getShareableLinks({
        productionId,
        expiryDate:
          QCConfirmationData?.productionResultDetails.shareableLinksExpireOn ??
          '',
        isRegenerate: true,
      });
    };

    const canShowNativelyPlaceholderErrorDocs =
      (QCConfirmationData?.productionResultDetails?.nativelyGeneratedDocs ??
        0) > 0;

    const disableAllActions =
      QCConfirmationData?.disableUnCommitAndAllActions ?? false;
    const currentProductionName =
      QCConfirmationData?.productionResultDetails?.productionName ||
      productionName ||
      '';
    const hasCommittedReproduction =
      productionsList?.productionItemDetails?.some((item) => {
        const isReproduction =
          item.productionTypeCode?.toUpperCase() === 'REPRODUCTION';
        const matchesPrior = item.priorProductionName === currentProductionName;
        const status = (item.status || '').toUpperCase().replace(/[\s_-]/g, '');
        return (
          isReproduction &&
          matchesPrior &&
          (status === ProductionStatus.COMPLETED ||
            status === ProductionStatus.INPROGRESS)
        );
      }) ?? false;
    const disableUncommitAfterReproduction =
      disableAllActions || (!isCommitAction && hasCommittedReproduction);

    return (
      <Box
        className="quality-form-section"
        component="form"
        data-testid="quality-form"
      >
        <Box className="quality-container">
          <Box className="quality-header">
            <Typography
              id="quality-header"
              className="title-text"
              data-testid={`quality-control-and-confirmation-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.QCConfirmation.title'
              )}
            </Typography>
          </Box>
          <Box className="quality-body" aria-labelledby="quality-body">
            <Box
              className="quality-production-container"
              aria-labelledby="quality-production-container"
            >
              <Box
                className="quality-production-info"
                aria-labelledby="quality-production-info"
              >
                <Typography
                  id="productionName"
                  className="typo-text subhead1_size custom-text-headings"
                  data-testid={`quality-control-and-confirmation-production-name`}
                >
                  {QCConfirmationData?.productionResultDetails?.productionName}
                </Typography>
                <Box
                  className="production-sub-container"
                  aria-labelledby="quality-production-info"
                >
                  <Typography
                    id="production-status-text"
                    className="typo-text label1_size custom-body-strong minWidthForText"
                    data-testid={`quality-control-and-confirmation-status`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.QCConfirmation.status'
                    )}
                  </Typography>
                  <Typography
                    id="production-status"
                    className="typo-text label1_size custom-text-success"
                    data-testid="production-status"
                  >
                    {
                      QCConfirmationData?.productionResultDetails
                        ?.productionStatus
                    }
                  </Typography>
                </Box>
                <Box
                  className="production-sub-container"
                  aria-labelledby="quality-production-info"
                >
                  <Typography
                    id="production-documents-generated-text"
                    className="typo-text label1_size custom-body-strong minWidthForText"
                    data-testid={`quality-control-and-confirmation-documents-generated`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.QCConfirmation.documentsGenerated'
                    )}
                  </Typography>
                  <Typography
                    id="production-documents-generated"
                    className="typo-text label1_size custom-text-headings"
                    data-testid="documents-generated"
                  >
                    {
                      QCConfirmationData?.productionResultDetails
                        ?.numberOfDocumentsGenerated
                    }
                  </Typography>
                </Box>
              </Box>
              <Box
                className="quality-button-container"
                aria-labelledby="quality-button-container"
              >
                <Box
                  className="quality-production-buttons"
                  aria-labelledby="quality-production-buttons"
                >
                  <ArrButton
                    variant="secondary"
                    size="large"
                    onClick={onDownloadBtnClick}
                    endIcon={<ArrowDropDownIcon />}
                    dataTestId="downloadBtn"
                    className="custom-button"
                    disabled={disableAllActions}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.QCConfirmation.download'
                    )}
                  </ArrButton>
                  <ArrButton
                    variant="secondary"
                    size="large"
                    onClick={() => setShowShareableLinksModal(true)}
                    dataTestId="getShareableLinkBtn"
                    isAriaDisabled={
                      disableAllActions ||
                      (QCConfirmationData &&
                        !QCConfirmationData?.productionResultDetails
                          .shareableLinksExpireOn)
                    }
                  >
                    {t(
                      'productionsAndExports.createNewProduction.QCConfirmation.getShareableLink'
                    )}
                  </ArrButton>
                  {isProductionSet && (
                    <ArrButton
                      variant="primary"
                      size="large"
                      onClick={() => handleCommitUncommit(isCommitAction)}
                      dataTestId="commitUncommitBtn"
                      isAriaDisabled={
                        disabledCommitOrUncommit ||
                        isAutoRefresh ||
                        !!QCConfirmationData?.disableCommitButton ||
                        disableUncommitAfterReproduction
                      }
                    >
                      {isCommitAction
                        ? t(
                            'productionsAndExports.createNewProduction.QCConfirmation.commit'
                          )
                        : t(
                            'productionsAndExports.createNewProduction.QCConfirmation.uncommit'
                          )}
                    </ArrButton>
                  )}
                </Box>
                {QCConfirmationData &&
                  !QCConfirmationData?.productionResultDetails
                    .productionArchivePath && (
                    <Box className="disable-message">
                      <Typography
                        id="download-disabled-msg"
                        className="typo-text body3_size disable-text"
                        data-testid="download-disabled-text"
                      >
                        {isProductionSet
                          ? t(
                              'productionsAndExports.createNewProduction.QCConfirmation.downloadDisableMsg'
                            )
                          : t(
                              'productionsAndExports.createNewProduction.QCConfirmation.downloadDisableMsgExport'
                            )}
                      </Typography>
                    </Box>
                  )}
                {disableAllActions && (
                  <Box className="disable-message">
                    <Typography
                      id="all-action-disabled-msg"
                      className="typo-text body3_size disable-text"
                      data-testid="all-action-disabled-text"
                    >
                      {t(
                        'productionsAndExports.createNewProduction.QCConfirmation.allActionDisableMsg'
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              className="quality-production-status"
              aria-labelledby="quality-production-status"
            >
              <TableContainer className="production-status-table">
                <Table>
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell className="table-cell border-right">
                        {t(
                          'productionsAndExports.createNewProduction.QCConfirmation.automatedCheck'
                        )}
                      </TableCell>
                      <TableCell className="table-cell">
                        {t(
                          'productionsAndExports.createNewProduction.QCConfirmation.statusTitle'
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {QCConfirmationData?.productionResultDetails?.qcResultDataList.map(
                      (qcItem, idx) => (
                        <TableRow key={qcItem.qcLabel || idx}>
                          <TableCell
                            className="table-body-cell"
                            title={qcItem.qcHelpText}
                          >
                            {qcItem.qcType}
                          </TableCell>
                          <TableCell className="table-body-cell success-body-cell">
                            {qcItem.qcResult}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {canShowNativelyPlaceholderErrorDocs && (
              <Box
                className="placeholder-error"
                aria-labelledby="placeholder-error"
              >
                <ArrButton
                  variant="text"
                  size="medium"
                  onClick={() => {
                    setOpenErrorGridModal(true);
                  }}
                  dataTestId="placeholderError"
                >
                  {t(
                    'productionsAndExports.createNewProduction.QCConfirmation.placeHolderError',
                    {
                      listOfError:
                        QCConfirmationData?.productionResultDetails
                          ?.nativelyGeneratedDocs ?? 0,
                    }
                  )}
                </ArrButton>
              </Box>
            )}
          </Box>
        </Box>
        <ArrMenu
          id="download-menu"
          ref={downloadMenuRef}
          className="quality-form-section__custom-download-menu"
          items={downloadMenu}
          onItemClick={onMenuItemClick}
          direction="bottom-left"
        />
        {shareableLinks && (
          <ArrModal
            showModal={showShareableLinksModal}
            showClose={true}
            onClose={() => setShowShareableLinksModal(false)}
            title={t(
              'productionsAndExports.createNewProduction.QCConfirmation.shareableLinksTitle'
            )}
            description={t(
              'productionsAndExports.createNewProduction.QCConfirmation.shareableLinksDesc1'
            )}
            dataTestId="shareLinksModal"
            className="quality-form-section__modal-container"
            buttons={[
              {
                id: '1',
                label: 'Regenerate Links',
                onClick: () => {
                  handleRegenerate();
                },
                variant: 'primary',
                dataTestId: 'regenerate-btn',
              },
            ]}
          >
            <Box className="modal-child-container">
              <Typography
                id="linkExpiry"
                className="typo-text body2_size link-expiry-text"
                data-testid={`quality-control-and-confirmation-shareable-links-desc2`}
              >
                {t(
                  'productionsAndExports.createNewProduction.QCConfirmation.shareableLinksDesc2',
                  {
                    linkExpiry: formatDateCommon(
                      shareableLinks.newExpiryDate,
                      'YYYY/MM/DD'
                    ),
                  }
                )}
              </Typography>
              <ArrTextField
                id="allFile"
                label={t(
                  'productionsAndExports.createNewProduction.QCConfirmation.allFiles'
                )}
                value={shareableLinks?.allFilesURL}
                sizeVariant="large"
                slotProps={{
                  input: {
                    'aria-label': t(
                      'productionsAndExports.createNewProduction.QCConfirmation.allFiles'
                    ),
                    'aria-labelledby': 'allFile',
                  },
                }}
                dataTestId={'allFile'}
              />
              <ArrTextField
                id="DATFileOnly"
                label={t(
                  'productionsAndExports.createNewProduction.QCConfirmation.DATFileOnly'
                )}
                value={shareableLinks?.datFileURL}
                sizeVariant="large"
                slotProps={{
                  input: {
                    'aria-label': t(
                      'productionsAndExports.createNewProduction.QCConfirmation.DATFileOnly'
                    ),
                    'aria-labelledby': 'DATFileOnly',
                  },
                }}
                dataTestId={'DATFileOnly'}
              />
              <Typography
                id="passwordTitle"
                className="typo-text body2_size password-title"
                data-testid={`quality-control-and-confirmation-password-title`}
              >
                {t(
                  'productionsAndExports.createNewProduction.QCConfirmation.passwordTitle'
                )}
                <br data-testid={`quality-control-and-confirmation-br`} />
                <span
                  className="typo-text body2_size custom-text-headings"
                  data-testid={`quality-control-and-confirmation-span`}
                >
                  {shareableLinks?.linkPassword}
                </span>
              </Typography>
            </Box>
          </ArrModal>
        )}
        {openErrorGridModal && (
          <ErrorGridModal
            productionId={productionId}
            securityGroupId={selectedSecurityGroupId}
            open={openErrorGridModal}
            onClose={() => setOpenErrorGridModal(false)}
            title={
              isProductionSet
                ? t(
                    'productionsAndExports.createNewProduction.generate.modals.errors.productionErrors'
                  )
                : t(
                    'productionsAndExports.createNewProduction.generate.modals.errors.exportErrors'
                  )
            }
            gridType={ErrorGridType.PlaceHolderErrors}
            productionName={productionName}
            dataTestId="qc-placeholder-error-grid-modal"
          />
        )}
      </Box>
    );
  }
);

QualityControlAndConfirmation.displayName = 'QualityControlAndConfirmation';

export default QualityControlAndConfirmation;
